import { StatusPill } from "./ui";
import { Store, Users, BookOpen, CreditCard, ChevronRight, Loader2, AlertOctagon } from "lucide-react";
import { useFetch } from "./api/useFetch";
import { adaptCompanyCard, unwrap } from "./api/adapters";
import { isSurfaceLive } from "./api/platformClient";
import { LiveBadge } from "./ui-badges";

export const ClientsDashboard = ({ clients: mockClients, onSelect }) => {
  const live = isSurfaceLive("COMPANIES");
  const { data, error, loading } = useFetch(live ? "/api/platform/companies" : null);

  const rawList = unwrap(data, "companies");
  const liveClients = Array.isArray(rawList) ? rawList.map(adaptCompanyCard) : null;
  const clients = live && liveClients ? liveClients : mockClients;

  return (
    <div data-testid="clients-dashboard">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Clients
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {live
              ? "All ShelfLife customer tenants under Blue Harbor Labs management."
              : "Mock customer tenants — live wiring enabled per flag."}
          </p>
        </div>
        <LiveBadge live={live} />
      </div>

      {live && loading && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="clients-loading">
          <Loader2 className="mx-auto h-6 w-6 text-slate-400 animate-spin" />
          <p className="mt-3 text-sm text-slate-500">Loading companies…</p>
        </div>
      )}

      {live && error && (
        <ErrorPanel error={error} />
      )}

      {(!live || (!loading && !error)) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {clients.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
              No companies returned by the platform.
            </div>
          )}
          {clients.map((c) => {
            const isTest = isLikelyTestTenant(c);
            return (
            <button
              type="button"
              key={c.id}
              data-testid={`client-card-${c.id}`}
              onClick={() => onSelect(c.id)}
              className={`group text-left rounded-xl border bg-white p-5 hover:border-[#0d3b66] hover:shadow-md transition-all ${
                isTest ? "border-dashed border-slate-300 opacity-75 hover:opacity-100" : "border-slate-200"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {c.headquarters}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="text-lg font-semibold text-slate-900 leading-tight truncate">
                      {c.name}
                    </div>
                    {isTest && (
                      <span
                        data-testid={`client-test-badge-${c.id}`}
                        className="text-[10px] uppercase tracking-wide font-semibold text-slate-600 bg-slate-100 border border-slate-300 rounded-full px-1.5 py-0.5 shrink-0"
                        title="Test tenant — created from the smoke/onboarding wizard. Safe to delete once lifecycle ships."
                      >Test</span>
                    )}
                  </div>
                </div>
                <StatusPill status={c.status} testId={`client-status-${c.id}`} />
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <Metric
                  icon={<Store className="h-4 w-4" />}
                  label="Stores"
                  value={c.storeCount}
                  liveAvailableInDetail={live && (!c.storeCount || c.storeCount === 0)}
                />
                <Metric
                  icon={<Users className="h-4 w-4" />}
                  label="Users"
                  value={c.userCount}
                  liveAvailableInDetail={live && (!c.userCount || c.userCount === 0)}
                />
                <Metric
                  icon={<BookOpen className="h-4 w-4" />}
                  label="Catalog"
                  value={
                    c.catalogStatus
                      ? <StatusPill status={c.catalogStatus} />
                      : <span className="text-xs text-slate-400 italic">Not reported</span>
                  }
                />
                <Metric
                  icon={<CreditCard className="h-4 w-4" />}
                  label="Billing"
                  value={
                    c.billingStatus
                      ? <StatusPill status={c.billingStatus} />
                      : <span className="text-xs text-slate-400 italic">Not reported</span>
                  }
                />
              </div>

              <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                <span>Last activity {c.lastActivity}</span>
                <span className="inline-flex items-center gap-1 text-[#0d3b66] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Open <ChevronRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Heuristic: a tenant is "likely test" when its slug/code or name contains
// the word "test". Used only for visual de-emphasis on the dashboard; never
// used as a precondition for any destructive operation.
const isLikelyTestTenant = (c) => {
  const code = (c.code || "").toLowerCase();
  const name = (c.name || "").toLowerCase();
  return code.includes("test") || /\btest\b/.test(name);
};

const Metric = ({ icon, label, value, liveAvailableInDetail }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2.5">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500">
      {icon}
      {label}
    </div>
    <div className="mt-1 text-sm font-semibold text-slate-900">
      {liveAvailableInDetail ? (
        <span
          className="text-xs italic text-slate-400 font-normal"
          title="The companies-list endpoint does not include this aggregate. Open the company to see the live value from /stats."
        >
          Open for details
        </span>
      ) : (
        typeof value === "object" ? value : value
      )}
    </div>
  </div>
);

const ErrorPanel = ({ error }) => (
  <div className="rounded-xl border border-rose-200 bg-rose-50 p-6" data-testid="clients-error">
    <div className="flex items-center gap-3">
      <AlertOctagon className="h-5 w-5 text-rose-700" />
      <div className="text-sm font-semibold text-rose-900">
        {error.kind === "forbidden" ? "Not authorized to list companies" :
         error.kind === "unauthorized" ? "Session expired" :
         error.kind === "network" ? "Cannot reach preview API" :
         "Companies request failed"}
      </div>
    </div>
    <p className="text-xs text-rose-800 mt-2">{error.message}</p>
  </div>
);
